import {
  ObservableQuery,
  OperationVariables,
  useMutation,
} from '@apollo/client';

import { ActivationOnboardingProgressDocument } from 'common/hooks/useActivationOnboardingProgress/ActivationOnboardingProgress.gql';
import useCompanyContext from 'common/hooks/useCompanyContext';
import useMainBankAccountId from 'common/hooks/useMainBankAccountId';
import { useVirtualCardOrderIdentifier } from 'features/PaymentCards/hooks/useVirtualCardOrderIdentifier';
import { usePostOnboardingAccountSetupContext } from 'features/PostOnboardingAccountSetup/context';

import { OrderVirtualTemporaryPaymentCardInput } from '../../../../../../../__generated__/GQL';
import { CreditCardsDocument } from '../../../../../hooks/useCreditCards/creditCards.gql';
import {
  OrderVirtualPermanentPaymentCardDocument,
  OrderVirtualPermanentPaymentCardMutation,
} from './orderVirtualPermanentPaymentCard.gql';
import {
  OrderVirtualTemporaryPaymentCardDocument,
  OrderVirtualTemporaryPaymentCardMutation,
} from './orderVirtualTemporaryPaymentCard.gql';

type CreditCard =
  | OrderVirtualPermanentPaymentCardMutation['orderVirtualPermanentPaymentCard']['creditCard']
  | OrderVirtualTemporaryPaymentCardMutation['orderVirtualTemporaryPaymentCard']['creditCard'];

export const useOrderVirtualPaymentCard = () => {
  const { companyProfileId, companyUserId } = useCompanyContext();
  const { mainBankAccountId } = useMainBankAccountId();
  const virtualCardOrderIdentifier = useVirtualCardOrderIdentifier();

  const { hasCompletedPostOnboardingSetup } =
    usePostOnboardingAccountSetupContext();

  if (!companyProfileId) {
    throw new Error(
      "'companyProfileId' is undefined in 'useOrderVirtualPaymentCard'",
    );
  }

  if (!companyUserId) {
    throw new Error(
      "'companyUserId' is undefined in 'useOrderVirtualPaymentCard'",
    );
  }

  const refetchQueries = [
    {
      query: CreditCardsDocument,
      variables: { companyProfileId, companyUserId },
    },
    {
      query: ActivationOnboardingProgressDocument,
      variables: {
        bankAccountId: mainBankAccountId || '',
        companyUserId: companyUserId || '',
      },
    },
  ];

  const handleOnQueryUpdated = (
    observableQuery: ObservableQuery<unknown, OperationVariables>,
  ) => {
    if (
      hasCompletedPostOnboardingSetup &&
      observableQuery.queryName === 'ActivationOnboardingProgress'
    ) {
      return false;
    }
    return observableQuery.refetch();
  };

  const [orderVirtualTemporaryPaymentCardMutation, hookTemporaryCard] =
    useMutation(OrderVirtualTemporaryPaymentCardDocument, {
      awaitRefetchQueries: true,
      onQueryUpdated: handleOnQueryUpdated,
      // Invalidate the cards list query to have the new card in it
      // when navigating back to the list after the order.
      refetchQueries,
    });

  const [orderVirtualPermanentPaymentCardMutation, hookPermanentCard] =
    useMutation(OrderVirtualPermanentPaymentCardDocument, {
      awaitRefetchQueries: true,
      onQueryUpdated: handleOnQueryUpdated,
      // Invalidate the cards list query to have the new card in it
      // when navigating back to the list after the order.
      refetchQueries,
    });

  const orderVirtualPaymentCard = async ({
    input,
    token,
  }: {
    input: Required<
      Pick<OrderVirtualTemporaryPaymentCardInput, 'bankAccountId'>
    > & { deactivateAt: string | null; alias?: string };
    token: string;
  }): Promise<{ creditCard?: Partial<CreditCard> }> => {
    const { deactivateAt } = input;

    const payload = {
      context: {
        headers: {
          'short-lived-token': token,
        },
      },
      onCompleted() {
        virtualCardOrderIdentifier.orderSucceeded();
      },
      variables: {
        input: {
          companyProfileId,
          companyUserId,
          ...input,
          clientOrderIdentifier: virtualCardOrderIdentifier.getCurrent(),
          deactivateAt: undefined,
        },
      },
    };

    if (deactivateAt) {
      const { data } = await orderVirtualTemporaryPaymentCardMutation({
        ...payload,
        variables: {
          input: {
            ...payload.variables.input,
            deactivateAt,
          },
        },
      });

      return { creditCard: data?.orderVirtualTemporaryPaymentCard.creditCard };
    }

    const { data } = await orderVirtualPermanentPaymentCardMutation(payload);
    return { creditCard: data?.orderVirtualPermanentPaymentCard.creditCard };
  };

  return {
    error: hookTemporaryCard.error || hookPermanentCard.error,
    loading: hookTemporaryCard.loading || hookPermanentCard.loading,
    orderVirtualPaymentCard,
  };
};
